import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare,
  Send,
  Loader2,
  Plus,
  Languages,
  Globe,
  ChevronDown,
  Briefcase,
  User,
  AlertCircle,
  Lock,
  ArrowLeft,
  Volume2
} from 'lucide-react';
import { CustomSvgIcon as SvgIcon } from '../components/common/Icons';
import api from '../lib/api';
import { toast } from 'sonner';
import { EmptyState } from '../components/common/EmptyState';
import { TranslationBadge } from '../components/messages/TranslationBadge';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTranslation } from '../i18n';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function MessagesPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [expandedMessages, setExpandedMessages] = useState({});
  const [ttsPlayingId, setTtsPlayingId] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // --- Data Fetching ---

  const { data: conversations, isLoading: convsLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.get('/conversations')
  });

  const { data: deals } = useQuery({
    queryKey: ['deals'],
    queryFn: () => api.get('/deals')
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients')
  });

  // Fetch deal details IF the selected conversation is a deal conversation
  // This is needed for Read-Only logic (e.g. check deal.status)
  const selectedConvObj = conversations?.find(c => c._id === selectedConversation);
  const isDealContext = selectedConvObj?.context_type === 'deal';
  const dealId = isDealContext ? selectedConvObj?.context_id : null;

  const { data: deal } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => api.get(`/deals/${dealId}`),
    enabled: !!dealId
  });

  const { data: statusInfo } = useQuery({
    queryKey: ['deal-status-info', dealId],
    queryFn: () => api.get(`/deals/${dealId}/status-info`),
    enabled: !!dealId
  });

  // --- WebSocket Hook Usage (Mirrors DealRoomPage) ---
  const { lastMessage, isConnected, error: wsError, sendMessage: wsSendMessage } = useWebSocket(selectedConversation);

  // Local state for typing indicators
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef({});

  const handleReadAloud = async (msg) => {
    const text = msg.original_text || msg.translated_text || '';
    if (!text.trim()) return;
    const lang = user?.preferred_language || 'en';
    setTtsPlayingId(msg._id);
    try {
      const res = await api.post('/ai/tts', { text: text.slice(0, 5000), lang });
      const audio = new Audio(api.getAudioUrl(res.audio_url));
      audio.onended = () => setTtsPlayingId(null);
      audio.onerror = () => { setTtsPlayingId(null); toast.error(t('messages.readAloud.error') || 'Playback failed'); };
      await audio.play();
    } catch (err) {
      toast.error(err.message || t('messages.readAloud.error') || 'Read aloud failed');
      setTtsPlayingId(null);
    }
  };

  // --- Messages Query ---
  const { data: messages, isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: () => selectedConversation ? api.get(`/conversations/${selectedConversation}/messages`) : Promise.resolve([]),
    enabled: !!selectedConversation,
    staleTime: Infinity // We rely on WebSocket for updates
  });

  // --- Mutations ---

  const sendMessageMutation = useMutation({
    mutationFn: (data) => api.post(`/conversations/${selectedConversation}/messages`, data),
    onSuccess: (newMessage) => {
      // Optimistic update via WebSocket is usually faster, but we ensure consistency here
      queryClient.setQueryData(['messages', selectedConversation], (old) => {
        if (!old) return [newMessage];
        if (old.some(m => m._id === newMessage._id)) return old;
        return [...old, newMessage];
      });

      // Immediately bubble the active conversation to the top of the list
      queryClient.setQueryData(['conversations'], (oldConvs) => {
        if (!oldConvs) return oldConvs;
        const idx = oldConvs.findIndex(c => c._id === selectedConversation);
        if (idx <= 0) return oldConvs; // already at top or not found
        const updated = [...oldConvs];
        const [conv] = updated.splice(idx, 1);
        return [{ ...conv, updated_at: new Date().toISOString() }, ...updated];
      });

      setMessageInput('');

      // Stop typing indicator immediately
      if (wsSendMessage) {
        wsSendMessage({ type: 'stop_typing', conversation_id: selectedConversation });
      }
    },
    onError: () => {
      toast.error('Failed to send message');
    }
  });

  const suggestReplyMutation = useMutation({
    mutationFn: () => api.post(`/conversations/${selectedConversation}/suggest-reply`, {}),
    onSuccess: (data) => {
      setMessageInput(data.suggested_text);
    },
    onError: (error) => {
      if (error?.status === 402 || error?.response?.status === 402) {
        navigate('/settings?tab=plan');
        toast.info('AI suggested replies require the Starter plan or higher.');
      } else {
        toast.error('Failed to generate reply suggestion');
      }
    }
  });

  // --- Effects ---

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedConversation, typingUsers]);

  // Handle WebSocket messages (New Message, Typing, Stop Typing)
  useEffect(() => {
    if (lastMessage && selectedConversation) {
      // Handle typing events
      if (lastMessage.type === 'typing') {
        // Don't show typing for self
        if (lastMessage.user_id === user?._id) return;

        setTypingUsers(prev => ({ ...prev, [lastMessage.user_id]: lastMessage.user_name }));

        // Clear existing timeout for this user
        if (typingTimeoutRef.current[lastMessage.user_id]) {
          clearTimeout(typingTimeoutRef.current[lastMessage.user_id]);
        }

        // Set new timeout to clear typing status (backup in case stop_typing is missed)
        // Increased to 6s to allow for a missing heartbeat
        typingTimeoutRef.current[lastMessage.user_id] = setTimeout(() => {
          setTypingUsers(prev => {
            const newState = { ...prev };
            delete newState[lastMessage.user_id];
            return newState;
          });
        }, 6000);
        return;
      }

      // Handle stop_typing events
      if (lastMessage.type === 'stop_typing') {
        if (lastMessage.user_id === user?._id) return;

        setTypingUsers(prev => {
          const newState = { ...prev };
          delete newState[lastMessage.user_id];
          return newState;
        });

        if (typingTimeoutRef.current[lastMessage.user_id]) {
          clearTimeout(typingTimeoutRef.current[lastMessage.user_id]);
        }
        return;
      }

      // Handle standard chat messages
      // Backend sends raw message object without 'type' wrapper for chat messages
      if (!lastMessage.type || lastMessage.type === 'message') {
        // Verify it belongs to this conversation (though WebSocket is scoped to room, validation is good)
        if (lastMessage.conversation_id === selectedConversation) {
          queryClient.setQueryData(['messages', selectedConversation], (oldMessages) => {
            if (!oldMessages) return [lastMessage];
            // Check duplication
            if (oldMessages.find(m => m._id === lastMessage._id)) return oldMessages;
            return [...oldMessages, lastMessage];
          });
        }
      }
    }
  }, [lastMessage, selectedConversation, queryClient, user?._id]);

  // Handle typing heartbeat
  useEffect(() => {
    let interval;
    if (messageInput && selectedConversation && wsSendMessage) {
      // Function to report typing status
      const reportTyping = () => {
        wsSendMessage({
          type: 'typing',
          user_id: user?._id,
          user_name: user?.name || user?.email || 'User',
          conversation_id: selectedConversation
        });
      };

      // Report immediately and then every 3 seconds
      reportTyping();
      interval = setInterval(reportTyping, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [messageInput, selectedConversation, wsSendMessage, user]);

  // Handle stop typing when input cleared
  useEffect(() => {
    if (!messageInput && selectedConversation && wsSendMessage) {
      wsSendMessage({
        type: 'stop_typing',
        user_id: user?._id,
        conversation_id: selectedConversation
      });
    }
  }, [messageInput, selectedConversation, wsSendMessage, user?._id]);

  // Clear message input and typing indicators when switching conversations
  useEffect(() => {
    setMessageInput('');

    // Clear all typing indicators so they don't bleed across conversations
    setTypingUsers({});

    // Clear all pending auto-clear timeouts for typing indicators
    Object.values(typingTimeoutRef.current).forEach(clearTimeout);
    typingTimeoutRef.current = {};
  }, [selectedConversation]);

  const location = useLocation();
  const navigate = useNavigate();

  // Auto-select first conversation OR from URL
  useEffect(() => {
    // We don't wait for conversations to load to clear selection, but we do need them to validate an ID
    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversation');

    if (conversationId) {
      // Only select if valid and loaded
      if (conversations?.some(c => c._id === conversationId)) {
        if (selectedConversation !== conversationId) {
          setSelectedConversation(conversationId);
        }
      }
    } else {
      // If no URL param, explicitly clear selection (Navigation Back)
      if (selectedConversation) {
        setSelectedConversation(null);
      }
    }
  }, [conversations, selectedConversation, location.search]);

  // --- Handlers ---

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedConversation) return;

    sendMessageMutation.mutate({
      conversation_id: selectedConversation,
      text: messageInput,
      // Target language handled by backend preference, but could be overridden if we added a selector
      // For now keeping it consistent with DealRoom which relies on profile/client settings usually
    });
  };

  const handleTyping = (e) => {
    setMessageInput(e.target.value);

    if (wsSendMessage && selectedConversation) {
      wsSendMessage({ type: 'typing', conversation_id: selectedConversation });
    }
  };

  const handleSuggestReply = () => {
    suggestReplyMutation.mutate();
  };

  const toggleMessageExpand = (msgId) => {
    setExpandedMessages(prev => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  const getConversationTitle = (conv) => {
    if (conv.context_type === 'deal') {
      const d = deals?.find(de => de._id === conv.context_id);
      return d?.title || 'Deal Conversation';
    }
    if (conv.context_type === 'client') {
      const c = clients?.find(cl => cl._id === conv.context_id);
      // If client is found in the business user's client list, display client name
      if (c) return c.name;
      // If no client is found, we might be the client logging in (where clients[] is empty)
      return conv.business_user_name || 'Business Representative';
    }
    return 'Conversation';
  };

  const getConversationSubtitle = (conv) => {
    if (conv.context_type === 'deal') {
      const d = deals?.find(de => de._id === conv.context_id);
      // Show deal type or status
      return d ? `${d.type?.replace('_', ' ')} • ${d.status}` : 'Deal';
    }
    if (conv.context_type === 'client') {
      const c = clients?.find(cl => cl._id === conv.context_id);
      if (!c) return t('messages.support') || 'Support';
      return t('messages.client') || 'Client';
    }
    return conv.context_type; // e.g., 'general'
  };

  // Logic to determine if input should be disabled (Read Only)
  const isReadOnly = useMemo(() => {
    if (!isDealContext) return false; // Non-deal conversations might assume always open?
    if (!deal) return false;

    const status = deal.status;
    // Replicating DealRoom logic: closed, cancelled are read-only
    if (['closed', 'cancelled'].includes(status)) return true;

    // Also check specific permissions if available
    if (statusInfo?.status_info && !statusInfo.status_info.can_send_messages) return true;

    return false;
  }, [deal, statusInfo, isDealContext]);


  const [searchQuery, setSearchQuery] = useState('');

  // ... (existing code)

  const filteredConversations = conversations?.filter(conv => {
    if (!searchQuery) return true;
    const title = getConversationTitle(conv).toLowerCase();
    const subtitle = getConversationSubtitle(conv).toLowerCase();
    const query = searchQuery.toLowerCase();
    return title.includes(query) || subtitle.includes(query);
  });

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4 animate-fade-in overflow-hidden">
      {/* Conversations List - Sidebar */}
      <Card className={`flex-shrink-0 flex flex-col border-r w-full md:w-80 ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-lg">{t("messages.conversations") || "Conversations"}</CardTitle>
          <div className="relative">
            <Input
              placeholder={t("messages.searchPlaceholder") || "Search messages..."}
              className="h-9 mt-2 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="flex-1 p-0 overflow-hidden">
          <ScrollArea className="h-full">
            {convsLoading ? (
              <div className="p-3 space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
              </div>
            ) : filteredConversations && filteredConversations.length > 0 ? (
              <div className="flex flex-col">
                {filteredConversations.map(conv => {
                  const isActive = selectedConversation === conv._id;
                  const title = getConversationTitle(conv);
                  const subtitle = getConversationSubtitle(conv);
                  const isDeal = conv.context_type === 'deal';

                  return (
                    <button
                      key={conv._id}
                      onClick={() => navigate(`?conversation=${conv._id}`)}
                      className={`w-full p-4 text-left transition-colors border-b last:border-0 hover:bg-muted/50 ${isActive ? 'bg-primary/5 border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10 border">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            <Briefcase className="h-5 w-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>
                            {title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate mt-1 capitalize">
                            {subtitle}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">{t("messages.noConversations") || "No conversations"}</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Area - Main */}
      <Card className={`flex-1 flex-col shadow-sm border overflow-hidden ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
        {selectedConversation ? (
          <>
            <CardHeader className="flex-shrink-0 border-b py-3 px-4 bg-card z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden -ml-2 h-8 w-8"
                    onClick={() => navigate('/messages')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <MessageSquare className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">
                      {conversations && getConversationTitle(conversations.find(c => c._id === selectedConversation))}
                    </CardTitle>
                    {isReadOnly && (
                      <Badge variant="outline" className="mt-1 text-xs font-normal text-muted-foreground gap-1">
                        <Lock className="h-3 w-3" /> Read Only
                      </Badge>
                    )}
                  </div>
                </div>
                {/* Optional: Add Deal Link if it is a deal */}
                {isDealContext && dealId && (
                  <Button variant="outline" size="sm" className="ml-auto gap-2 text-muted-foreground hover:text-foreground" asChild>
                    <Link to={`/deals/${dealId}`}>
                      View Deal <Briefcase className="h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            </CardHeader>

            <div
              ref={scrollContainerRef}
              className="flex-1 p-4 bg-muted/5 overflow-y-auto overflow-x-hidden min-h-0"
              data-testid="messages-scroll-area"
            >
              {messagesLoading ? (
                <div className="space-y-4 p-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                      <Skeleton className="h-16 w-64 rounded-xl" />
                    </div>
                  ))}
                </div>
              ) : messages && messages.length > 0 ? (
                <div className="space-y-6 w-full overflow-x-hidden">
                  {messages.map((msg) => {
                    // Message Bubble Logic Copied from DealRoomPage
                    const isUser = msg.from?.type === 'user';
                    const isMe = isUser && msg.from?.id === user?._id;
                    const isExpanded = expandedMessages[msg._id];
                    const hasTranslation = msg.translated_text && msg.translated_text !== msg.original_text;

                    let senderName = msg.sender_name || '';
                    if (!senderName) {
                      if (isMe) {
                        senderName = user?.name || (t ? t('dealRoom.conversation.you') : 'You');
                      } else if (isUser) {
                        senderName = (t ? t('dealRoom.conversation.colleague') : 'Colleague');
                      } else {
                        const client = clients?.find(c => c._id === msg.from?.id);
                        senderName = client?.name || (t ? t('dealRoom.conversation.client') : 'Client');
                      }
                    }

                    const displayName = isMe ? (t ? t('dealRoom.conversation.you') : 'You') : senderName;
                    const senderInitials = senderName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '??';

                    return (
                      <div
                        key={msg._id}
                        className={`flex items-end gap-2 w-full min-w-0 ${isMe ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${msg._id}`}
                      >
                        {!isMe && (
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {senderInitials}
                            </AvatarFallback>
                          </Avatar>
                        )}

                        <div className={`min-w-0 flex flex-col ${isMe ? 'w-fit max-w-[80%] items-end' : 'max-w-[80%]'}`}>
                          <div className={`flex items-center gap-2 mb-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <span className="text-xs text-muted-foreground">{displayName}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.sent_at || msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleReadAloud(msg)}
                              disabled={ttsPlayingId === msg._id || !(msg.original_text || msg.translated_text)}
                              className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-50"
                              title={t('messages.readAloud.title') || 'Read aloud'}
                              aria-label={t('messages.readAloud.title') || 'Read aloud'}
                            >
                              {ttsPlayingId === msg._id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Volume2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                          <div className={`rounded-xl p-3 shadow-sm max-w-full ${isMe ? 'bg-primary text-primary-foreground rounded-br-none text-right w-fit' : 'bg-white dark:bg-muted border rounded-bl-none w-fit'}`}>
                            <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">
                              {isMe ? msg.original_text : (msg.translated_text || msg.original_text)}
                            </p>
                            {hasTranslation && !isMe && (
                              <button
                                onClick={() => toggleMessageExpand(msg._id)}
                                className="text-xs mt-2 flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity"
                              >
                                <Globe className="h-3 w-3" />
                                {isExpanded ? (t ? t('dealRoom.messages.hideOriginal') : 'Hide Original') : (t ? t('dealRoom.messages.showOriginal') : 'Show Original')}
                                <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                            {isExpanded && hasTranslation && (
                              <p className="text-xs mt-2 pt-2 border-t border-current/20 opacity-70 break-words">
                                Original ({msg.original_language?.toUpperCase()}): {msg.original_text}
                              </p>
                            )}
                          </div>
                        </div>

                        {isMe && (
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                              {senderInitials}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-8 opacity-60">
                  <SvgIcon src="discussion.svg" className="h-20 w-20 opacity-30 mb-4" />
                  <p className="text-muted-foreground font-medium">No messages yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Start the conversation by typing below</p>
                </div>
              )}
            </div>

            {/* Input Area */}
            {isReadOnly ? (
              <div className="p-4 border-t bg-muted/20">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground p-3 rounded-md bg-muted/50 border border-dashed">
                  <Lock className="h-4 w-4" />
                  <span>Messaging is disabled for this conversation</span>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t bg-background space-y-3">
                <div className="relative">
                  <Textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder={t('dealRoom.messages.placeholder') || "Type your message..."}
                    className="min-h-[60px] max-h-[120px] resize-none pr-12 text-base"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  {/* Typing Indicator Overlay (Matches DealRoomPage) */}
                  {Object.keys(typingUsers).length > 0 && (
                    <div className="absolute bottom-full left-0 mb-4 px-1 z-50 pointer-events-none">
                      <p className="text-xs text-muted-foreground animate-pulse font-medium bg-background/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm">
                        {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSuggestReply}
                      disabled={suggestReplyMutation.isPending}
                      className="text-xs h-8"
                    >
                      {suggestReplyMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <SvgIcon src="bulb.svg" className="h-3 w-3 mr-1" />}
                      AI Suggest
                    </Button>
                  </div>

                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || sendMessageMutation.isPending}
                    size="sm"
                    className="h-8 px-4"
                  >
                    {sendMessageMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    {t("messages.send") || "Send"}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-muted/5">
            <EmptyState
              icon={MessageSquare}
              title={t("messages.selectConversation") || "Select a Conversation"}
              description={t("messages.selectConversationDesc") || "Choose a conversation from the sidebar to start chatting"}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
